import { useAuth } from '@/contexts/AuthContext';
import { Conversation, Content } from '@shared/types';
import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

const defaultConversation: Conversation = {
  id: '',
  title: '',
  current_message_leaf_id: null,
  user_id: '',
  created_at: '',
  updated_at: '',
};

export function useConversation() {
  const { id: conversationId } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: conversation, isLoading: isConversationLoading } =
    useQuery<Conversation>({
      queryKey: ['conversation', conversationId],
      enabled: !!conversationId && !!user,
      refetchOnMount: false,
      queryFn: async () => {
        if (!conversationId) {
          throw new Error('Conversation ID is required');
        }
        return api.getConversation(conversationId);
      },
    });

  const { mutate: updateConversation, mutateAsync: updateConversationAsync } =
    useMutation({
      mutationFn: async (conv: Conversation) => {
        return api.updateConversation(conv.id, conv);
      },
      onMutate: async (conv) => {
        await queryClient.cancelQueries({
          queryKey: ['conversation', conv.id],
        });

        const oldConversation = queryClient.getQueryData<Conversation>([
          'conversation',
          conv.id,
        ]);

        queryClient.setQueryData(['conversation', conv.id], conv);

        return { oldConversation };
      },
      onSuccess: (data) => {
        queryClient.setQueryData(['conversation', conversationId], data);
        queryClient.invalidateQueries({
          queryKey: ['conversations'],
        });
      },
      onError: (_error, conv, context) => {
        queryClient.setQueryData(
          ['conversation', conv.id],
          context?.oldConversation,
        );
      },
    });

  return {
    conversation: conversation ?? defaultConversation,
    isConversationLoading,
    updateConversation,
    updateConversationAsync,
  };
}

export async function generateConversationTitle(
  conversationId: string,
  content: Content,
): Promise<string> {
  try {
    const result = await api.generateTitle(conversationId, content);
    return result.title || 'New Conversation';
  } catch {
    return 'New Conversation';
  }
}
